import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      // Lower-case Invalid → "invalid…" feels softer + leaves room for the recovery CTA
      setErr(detail);
    } finally {
      setLoading(false);
    }
  };

  const isInvalidCreds = /invalid email or password/i.test(err);

  return (
    <div className="min-h-screen noise grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--brand-ink)] text-white">
        <Link to="/" className="font-display text-2xl font-bold" data-testid="login-brand">JobVault</Link>
        <div>
          <p className="label-tiny text-[var(--pastel-yellow)] mb-3">welcome back</p>
          <h2 className="font-display text-5xl font-semibold leading-[1.05]">
            log in. <br/>
            <span className="text-[var(--pastel-yellow)]">apply faster.</span><br/>
            touch grass.
          </h2>
          <p className="mt-8 text-white/70 max-w-md leading-relaxed">
            your vault remembers everything so you don&apos;t have to. seriously, it&apos;s been waiting.
          </p>
        </div>
        <p className="font-mono-d text-xs text-white/40">est. 2026 · the cv ends here</p>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form" autoComplete="on">
          <p className="label-tiny text-[var(--brand-muted)] mb-2">/login</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight mb-8">welcome back, applicant.</h1>

          {err && (
            <div data-testid="login-error" className="card-hard rounded-md p-3 bg-[var(--pastel-peach)] text-sm mb-4">
              <p>{err}</p>
              {isInvalidCreds && (
                <p className="mt-2 text-xs text-[var(--brand-ink)]/75">
                  tip: if your browser auto-filled the password, double-check the eye-icon — sometimes saved logins go stale. still stuck?{" "}
                  <Link to="/forgot-password" data-testid="login-error-reset"
                    className="underline font-medium text-[var(--brand-ink)]">reset it in 30 seconds →</Link>
                </p>
              )}
            </div>
          )}

          <label className="label-tiny block mb-2" htmlFor="login-email">email</label>
          <input id="login-email" data-testid="login-email" name="email"
            type="email" required autoComplete="username"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full card-hard rounded-md px-4 py-3 mb-4 focus:ring-2 focus:ring-[var(--brand-primary)]" />

          <label className="label-tiny block mb-2" htmlFor="login-password">password</label>
          <div className="relative mb-2">
            <input id="login-password" data-testid="login-password" name="password"
              type={showPw ? "text" : "password"} required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full card-hard rounded-md px-4 py-3 pr-12 focus:ring-2 focus:ring-[var(--brand-primary)]" />
            <button type="button" onClick={() => setShowPw((v) => !v)}
              data-testid="login-show-password"
              aria-label={showPw ? "hide password" : "show password"}
              title={showPw ? "hide password" : "show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded hover:bg-[var(--pastel-yellow)]">
              {showPw ? <EyeSlash size={18}/> : <Eye size={18}/>}
            </button>
          </div>

          <div className="flex justify-end mb-6">
            <Link to="/forgot-password" data-testid="login-forgot-link"
              className="text-xs text-[var(--brand-muted)] underline hover:text-[var(--brand-ink)]">
              forgot password?
            </Link>
          </div>

          <button data-testid="login-submit" disabled={loading} type="submit"
            className="w-full btn-hard bg-[var(--brand-primary)] text-white rounded-md py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? "unlocking…" : <>log in <ArrowRight weight="bold" size={16}/></>}
          </button>

          <p className="mt-6 text-sm text-[var(--brand-muted)]">
            no vault yet? <Link to="/register" data-testid="login-to-register" className="text-[var(--brand-ink)] underline font-medium">start one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
